import { WorkflowRestrictionToggle } from './WorkflowRestrictionToggle'
import type { RecordControlProps } from './recordControlProps'
import type { RecordAccessEntity } from './recordGrantsQuery'
import type { RecordGrantSubject } from './useUpdateRecordAccessGrant'
import type { PermissionSetEntity } from '@omnea-digital/authz-model'
import type { ComponentType } from 'react'

type EditableKind = RecordGrantSubject['kind']

export const TAB_BY_KIND = {
  user: 'users-and-groups',
  group: 'users-and-groups',
  role: 'roles',
  user_property: 'user-attributes',
} as const satisfies Record<EditableKind, string>

export type AccessTab = (typeof TAB_BY_KIND)[EditableKind]

type AccessDialogBehaviour = {
  /** A role grant the endpoint reports on an entity without a `roles` tab is not shown. */
  tabs: readonly AccessTab[]
  recordControl: ComponentType<RecordControlProps> | null
}

/** What the Manage access dialog offers on each entity, beyond what the authz model decides. */
export const ACCESS_DIALOG_BY_ENTITY: Record<PermissionSetEntity, AccessDialogBehaviour> = {
  request: { tabs: ['users-and-groups'], recordControl: null },
  supplier: { tabs: ['users-and-groups'], recordControl: null },
  agreement: { tabs: ['users-and-groups'], recordControl: null },
  bill: { tabs: ['users-and-groups'], recordControl: null },
  workflow: { tabs: ['users-and-groups', 'roles', 'user-attributes'], recordControl: WorkflowRestrictionToggle },
}

/** The groups `RecordAccessSubjectPicker` lists subjects under: a department or subsidiary is a `user_property` subject. */
export type SubjectKind = 'user' | 'group' | 'role' | 'department' | 'subsidiary'

/**
 * Which subjects `RecordAccessSubjectPicker` offers on each entity, in this order. An entity offers only what its
 * record-access endpoint accepts, so a kind added here for one entity must already be accepted there. Departments and
 * subsidiaries are offered on dashboards only: workflows grant them from their user-attributes tab, and every other
 * entity refuses them.
 */
export const SUBJECT_KINDS_BY_ENTITY: Record<RecordAccessEntity, readonly SubjectKind[]> = {
  request: ['user', 'group'],
  supplier: ['user', 'group'],
  agreement: ['user', 'group'],
  bill: ['user', 'group'],
  workflow: ['user', 'group', 'role'],
  document: ['user', 'group'],
  insights_dashboard: ['user', 'group', 'department', 'subsidiary'],
}
